/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-04-16
 * Updated: 2025-06-13 - Improved layout and user experience
 */

// Add third-party dependencies.
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, Typography, Alert, Grid, IconButton, Paper, useMediaQuery, useTheme, Chip, Stack } from '@mui/material';

// Add local dependencies.
import { useGetRecitalVideosQuery } from '../../api/publicSlice';
import Icons from '../Common/Icons';
import VideoPlayer from '../Common/VideoPlayer';

// Years with published recital videos. Add new years here as they're released.
const AVAILABLE_RECITAL_YEARS = [2026, 2025, 2024];

// Set columns with dynamic renderCell for the Play column
const getColumns = (selectedId) => [
  {
    field: 'id',
    headerName: 'Play',
    width: 70,
    align: 'center',
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Box pt={1} display="flex" alignItems="center" justifyContent="center">
        {params.row.id === selectedId ? (
          <Chip
            icon={<Icons iconName="PlayCircleFilled" title="Now Playing" />}
            label="Playing"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        ) : (
          <Icons iconName="PlayCircleFilled" title="Play Video" />
        )}
      </Box>
    ),
  },
  { field: 'title', headerName: 'Title', width: 250 },
  { field: 'show_day', headerName: 'Show Day', width: 150 },
  { field: 'show_time', headerName: 'Show Time', width: 150 },
  { field: 'teacher', headerName: 'Teacher', width: 150 },
  { field: 'class_name', headerName: 'Class', width: 150 },
  { field: 'team', headerName: 'Team', width: 200 },
];

/**
 * RecitalVideos component.
 * @example
 * return (
 *   <RecitalVideos />
 * )
 * @returns {React.ReactElement} component.
 */
const RecitalVideos = () => {
  // Get environment variables.
  const videoUrl = process.env.REACT_APP_VIDEO_URL;

  // Theme and responsive hooks
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Router hooks.
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const pathParts = pathname.split('/').filter((t) => t);
  const year = pathParts[1];

  // State for selected video name and ID
  const [videoName, setVideoName] = useState(pathParts.length > 2 ? `${pathParts[2]}/${pathParts[3]}` : undefined);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [showVideo, setShowVideo] = useState(!!videoName);

  // API hooks.
  const { isLoading, isError, error, isSuccess, data } = useGetRecitalVideosQuery(year);

  // Set video name and update selected ID when URL changes
  useEffect(() => {
    const newPathParts = pathname.split('/').filter((t) => t);
    const newVideoName = newPathParts.length > 2 ? `${newPathParts[2]}/${newPathParts[3]}` : undefined;
    setVideoName(newVideoName);
    setShowVideo(!!newVideoName);

    // Find the selected video ID if we have data and a video name
    if (data && newVideoName) {
      const selectedVideo = data.find(
        (video) => `/recital-videos/${year}/recital-${year}/${video.file_name}` === pathname
      );
      setSelectedVideoId(selectedVideo?.id || null);
    }
  }, [pathname, data, year]);

  /**
   * Handle row click event.
   * @param {Object} params - The event parameters.
   * @param {Object} params.row - The clicked row object.
   * @returns {void}
   */
  const handleRowClick = (params) => {
    const { row } = params;
    setSelectedVideoId(row.id);
    navigate(`/recital-videos/${year}/recital-${year}/${row.file_name}`);
  };

  /**
   * Handle close video button click.
   * @returns {void}
   */
  const handleCloseVideo = () => {
    setShowVideo(false);
    // Keep the URL as is, just hide the video
  };

  /**
   * Handle back to list button click (for mobile).
   * @returns {void}
   */
  const handleBackToList = () => {
    setShowVideo(false);
  };

  // Set video URL.
  const videoFiles = videoName
    ? [
        {
          id: 0,
          src: `${videoUrl}/${videoName}.mp4`,
          type: 'video/mp4',
        },
        {
          id: 1,
          src: `${videoUrl}/${videoName}.webm`,
          type: 'video/webm',
        },
      ]
    : [];

  // Get columns with current selected ID
  const columns = getColumns(selectedVideoId);

  // Render component.
  return (
    <Box p={3}>
      <Typography variant="h1">{year} Recital Videos</Typography>

      {AVAILABLE_RECITAL_YEARS.filter((y) => String(y) !== String(year)).length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Other years:
          </Typography>
          {AVAILABLE_RECITAL_YEARS.filter((y) => String(y) !== String(year)).map((y) => (
            <Chip
              key={y}
              label={y}
              size="small"
              clickable
              variant="outlined"
              onClick={() => navigate(`/recital-videos/${y}`)}
            />
          ))}
        </Stack>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.error || error?.message || 'There was a problem loading the recital videos.'}
        </Alert>
      )}

      {isSuccess && (
        <Grid container spacing={3}>
          {/* Video Player Section - Full width on mobile, half width on desktop */}
          {videoName && showVideo && (
            <Grid item xs={12} md={isDesktop ? 6 : 12}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  position: 'relative',
                  height: isDesktop ? '100%' : 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden', // Prevent content from overflowing
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" noWrap sx={{ maxWidth: '80%' }}>
                    {data?.find((video) => video.id === selectedVideoId)?.title || 'Video Player'}
                  </Typography>
                  <Box>
                    {!isDesktop && (
                      <IconButton
                        onClick={handleBackToList}
                        aria-label="Back to video list"
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <Icons iconName="ExpandLess" title="Back to List" />
                      </IconButton>
                    )}
                    <IconButton onClick={handleCloseVideo} aria-label="Close video player" size="small">
                      <Icons iconName="Close" title="Close" />
                    </IconButton>
                  </Box>
                </Box>
                <Box
                  flexGrow={1}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden', // Prevent content from overflowing
                  }}
                >
                  <VideoPlayer key={videoName} videoFiles={videoFiles} responsive />
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Data Grid Section - Full width when no video or on mobile, half width on desktop with video */}
          <Grid item xs={12} md={isDesktop && videoName && showVideo ? 6 : 12}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <DataGrid
                columns={columns}
                rows={data}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 25,
                    },
                  },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                onRowClick={handleRowClick}
                loading={isLoading}
                components={{
                  Toolbar: GridToolbar,
                }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                sx={{
                  '& .MuiDataGrid-row': {
                    cursor: 'pointer',
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.action.selected,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    },
                  },
                  minHeight: '455px',
                  height: isDesktop ? '70vh' : '455px',
                }}
                density="compact"
                getRowClassName={(params) => (params.row.id === selectedVideoId ? 'Mui-selected' : '')}
              />
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

// Export component.
export default RecitalVideos;
